/**
 * Sustitución de tildes según la fuente CRT en uso.
 *
 * Las fuentes pixel-perfect que usamos NO tienen renderizado correcto
 * de TODAS las tildes — cada una tiene un subconjunto distinto:
 *  - "Perfect DOS VGA 437" original: outlines rotos en tildes
 *    mayúsculas + minúsculas con ñ y ¿¡.
 *  - "Perfect DOS VGA 437 Win" (la actual default): solo mayúsculas
 *    acentuadas rotas. Minúsculas y ñ ¿ ¡ se renderizan bien.
 *  - "Amstrad CPC464": SÍ tiene tildes (verificado 2026-05-12). No
 *    se sustituye nada.
 *
 * Aplicamos `dosify(s)` a todo el texto que va al render para evitar
 * que se vea un cuadrado tofu cuando el glifo está roto.
 *
 * Detectamos la fuente activa leyendo `getComputedStyle` del propio
 * terminal — Vite no tiene API para introspectar @font-face cargados.
 */

const FONT_SUBSTITUTIONS: Record<string, Record<string, string>> = {
  'Perfect DOS VGA 437 Win': { 'Á': 'A', 'Í': 'I', 'Ó': 'O', 'Ú': 'U' },
  'Perfect DOS VGA 437': {
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ñ': 'n',
    'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ñ': 'N',
    'ü': 'u', 'Ü': 'U', '¿': '?', '¡': '!',
  },
  // Amstrad CPC464 NO se incluye: la fuente sí tiene los glifos de
  // tildes y ñ ¿ ¡, las dejamos tal cual para que el texto sea natural.
};

let activeSubstitutions: Record<string, string> = {};

/**
 * Detecta qué fuente está pintando el terminal y guarda el mapa de
 * sustituciones aplicable. Espera a `document.fonts.ready` para que
 * la fuente custom haya sido cargada (sino lee la fallback monospace).
 */
export async function detectActiveSubstitutions(terminalEl: HTMLElement): Promise<void> {
  if (document.fonts && document.fonts.ready) {
    await document.fonts.ready;
  }
  const used = getComputedStyle(terminalEl)
    .fontFamily.split(',')[0]
    ?.replace(/['"]/g, '')
    .trim();
  if (used && used in FONT_SUBSTITUTIONS) {
    activeSubstitutions = FONT_SUBSTITUTIONS[used] ?? {};
  } else {
    activeSubstitutions = {};
  }
}

/** Aplica las sustituciones detectadas. */
export function dosify(s: string): string {
  if (!s) return s;
  let out = s;
  for (const k in activeSubstitutions) {
    out = out.split(k).join(activeSubstitutions[k] ?? k);
  }
  return out;
}
